import { useState, useCallback, useRef, useEffect } from 'react'
import { getCurrentTrips } from '../services/currentTrips'
import { compareTripList, subtractList } from '../utils/filterTripList'
import PanicAlert from '../assets/sounds/Alert.mp3'
import MonitorAlert from '../assets/sounds/monitoreo.mp3'

const useCurrentTrips = () => {
  const [trips, setTrips] = useState([])
  const [loading, setLoading] = useState(false)

  const [monitoring, setMonitoring] = useState(false)
  const [panicAlert, setPanicAlert] = useState(false)

  const tripsRef = useRef(trips)
  const alertTriggeredRef = useRef({})

  useEffect(() => {
    tripsRef.current = trips
  }, [trips])

  /**
   * Fetches and updates trip data,
   * comparing it to the previous state, and potentially
   * triggering an alert based on differences in the data
   */
  const getTrips = useCallback(async () => {
    try {
      setLoading(true)
      const latestTrips = await getCurrentTrips()

      if (compareTripList(latestTrips, tripsRef.current)) {
        setMonitoring(false)
        setPanicAlert(false)
        return
      }

      setTrips(latestTrips)

      const substraction = subtractList(latestTrips, tripsRef.current)
      launchAlert(substraction)
    } catch (err) {
      throw new Error(`Error in getTrips: ${err}`)
    } finally {
      setLoading(false)
    }
  }, [])



  /**
   * Processes an array of trip objects and launches different types
   * of alerts (panic or monitoring) based on the properties of these objects.
   * It also keeps track of which trips have had alerts triggered to prevent duplicate alerts for the same trip.
   * @param {Array<Object>} trips
   */
  const launchAlert = (trips) => {
    trips.forEach((trip) => {
      if (trip.panic) {
        triggerSound(PanicAlert)
        setPanicAlert(trip.panic)
        return
      }

      if (!alertTriggeredRef.current[trip.tripID] && trip.monitoring) {
        triggerSound(MonitorAlert)
        setMonitoring(trip.monitoring)
        alertTriggeredRef.current[trip.tripID] = true
        return
      }
    })
  }

  /**
   * Triggers sound effects, such as alerts or other auditory feedback, in a web application
   * @param {String} audioSrc - Path to the local sound asset
   */
  const triggerSound = (audioSrc) => {
    const audio = new Audio()
    audio.pause()
    audio.muted = false
    audio.src = audioSrc
    audio.currentTime = 0
    audio.play()
  }

  return { trips, getTrips, loading, monitoring, panicAlert }
}

export default useCurrentTrips